import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSelect, useSwitch, useViewport } from '@chilecompra/react-kit'
import { Card, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import IconButton from '../../components/Buttons/IconButton'
import ProtectByFeatures from '../../security/ProtectByFeatures'
import generateYearsBetween from '../../modules/utils/years'
import { REACT_APP_MP_URL } from '../../config/settings/environment'
import { formatAmount } from '../../modules/utils/formatters'
import { onShowContingenciesInformationDialog } from '../ContingencyExpenseDialog/ContingencyExpenseDialog.actions'
import { onGetContingencyExpenseThunk } from './ContingencyExpenseSection.actions'
import { Content, Divider, GridContainer, GridItem, Link, Root, TextFooter } from './ContingencyExpenseSection.styles'

/**
 * The ContingencyExpenseSection's container.
 */
const ContingencyExpenseSection = () => {
  const {
    size: { isUpToMedium, isDownToMedium }
  } = useViewport()
  const dispatch = useDispatch()
  const [startYear, setStartYear] = useState(undefined)
  const [endYear, setEndYear] = useState(undefined)
  const { contingencyOptions, contingencySelected } = useSelector(state => state.contingencies)
  const { contingencyExpense } = useSelector(state => state.contingencyExpense)

  const getYearOptions = contingencyOptions?.find(option => option.value === contingencySelected)
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])

  const { value, onChange } = useSwitch()
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    overwriteCallback: () => endYear
  })

  const handleShowDialog = useCallback(() => dispatch(onShowContingenciesInformationDialog({ showDialog: true })), [])

  useEffect(() => {
    dispatch(onGetContingencyExpenseThunk({ contingencyId: contingencySelected, year: yearValue }))
  }, [contingencySelected, yearValue])

  useEffect(() => {
    setStartYear(getYearOptions?.startYear)
    setEndYear(getYearOptions?.endYear)
  }, [getYearOptions])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Typography
              color="primary"
              fontSize="18px"
              fontWeight="bold"
              lineHeight="21px"
              margin={isUpToMedium ? '96px 0 0' : '48px 0 24px'}
              variant="body1"
            >
              Gasto del Estado de Chile
            </Typography>
            <Typography
              fontSize="34px"
              fontWeight="bold"
              lineHeight="38px"
              margin={isUpToMedium ? '12px 0' : '0'}
              variant="h2"
              width={isUpToMedium ? '485px' : undefined}
            >
              Cifras generales de las compras asociadas a COVID-19
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={isUpToMedium ? 5 : 12}>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0 0 48px' : '24px 0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Revisa las cifras acumuladas de las compras realizadas por el Estado de Chile en materia de COVID-19.
            </Typography>
          </GridItem>
          <GridItem xs={isUpToMedium ? 7 : 12}>
            <Typography fontSize="18px" lineHeight="24px" variant="body1" width={isUpToMedium ? '448px' : undefined}>
              Toda la información sobre compras públicas se obtiene desde las órdenes de compra generadas en{' '}
              <Link href={REACT_APP_MP_URL}>Mercado Público</Link>.
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer justifyContent="center" margin="24px 0 0">
          <GridContainer alignItems="center" justifyContent="space-between" width={isUpToMedium ? '850px' : '100%'}>
            <GridItem xs={12} md={2}>
              <Select label="AÑO" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
            </GridItem>
            <GridItem xs={12} md={2} container={isDownToMedium} justifyContent={isUpToMedium ? 'flex-start' : 'center'}>
              <Switch checked={value} color="primary" labelLeft="CLP" labelRight="USD" onChange={onChange} />
            </GridItem>
            <Card color="blank" elevation="1">
              <GridContainer minHeight="179px">
                <GridItem
                  xs={12}
                  md={7}
                  container={isDownToMedium}
                  justifyContent={isUpToMedium ? 'flex-start' : 'center'}
                >
                  <Typography
                    color="primary"
                    fontSize="34px"
                    fontWeight="bold"
                    lineHeight="38px"
                    margin={isUpToMedium ? '48px 0 0 96px' : '24px 0 0'}
                    variant="h1"
                  >
                    {value ? '$USD' : '$'}{' '}
                    {formatAmount(value ? contingencyExpense?.amountUSD : contingencyExpense?.amountCLP)}
                  </Typography>
                  <Typography
                    color="black2"
                    fontSize="16px"
                    lineHeight="24px"
                    margin={isUpToMedium ? '0 0 0 96px' : '0 0 24px'}
                    variant="body1"
                  >
                    Monto transado durante el año
                  </Typography>
                </GridItem>
                {isUpToMedium && (
                  <GridItem md container justifyContent="center">
                    <Divider orientation="vertical" />
                  </GridItem>
                )}
                <GridItem
                  xs={12}
                  md={4}
                  container={isDownToMedium}
                  justifyContent={isUpToMedium ? 'flex-start' : 'center'}
                >
                  <Typography
                    color="primary"
                    fontSize="34px"
                    fontWeight="bold"
                    lineHeight="38px"
                    margin={isUpToMedium ? '48px 0 0' : '24px 0 0'}
                    variant="h1"
                  >
                    {formatAmount(contingencyExpense?.qtyOC)}
                  </Typography>
                  <Typography
                    color="black2"
                    fontSize="16px"
                    lineHeight="24px"
                    margin={isUpToMedium ? undefined : '0 0 24px'}
                    variant="body1"
                  >
                    Número de órdenes de compra
                  </Typography>
                </GridItem>
              </GridContainer>
            </Card>
            <Card color="blank" elevation="1" margin={isUpToMedium ? '24px 0 96px' : '32px 0 48px'} padding="12px 24px">
              <GridContainer alignItems="center" justifyContent={isUpToMedium ? 'space-between' : 'center'}>
                <IconButton type="question" margin={isDownToMedium ? '24px 0' : undefined} onClick={handleShowDialog}>
                  <TextFooter>¿CÓMO SE OBTIENE ESTA INFORMACIÓN?</TextFooter>
                </IconButton>
                <ProtectByFeatures rule={features => features['contingencies.expense.download']}>
                  <IconButton type="download" margin={isDownToMedium ? '0 0 24px' : undefined}>
                    <TextFooter>DESCARGA ESTA INFORMACIÓN</TextFooter>
                  </IconButton>
                </ProtectByFeatures>
              </GridContainer>
            </Card>
          </GridContainer>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ContingencyExpenseSection
